import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { OrderResponse, PaginationMeta } from '../useOrders';

export interface OrderFilters {
  user_id?: number;
  status?: string;
  order_status?: string;
  payment_status?: string;
  shipment_status?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  page?: number;
  per_page?: number;
  order_by?: string;
  order?: 'asc' | 'desc';
}

export interface OrderStats {
  total_orders: number;
  pending_orders: number;
  processing_orders: number;
  shipped_orders: number;
  delivered_orders: number;
  cancelled_orders: number;
  total_revenue: number;
}

export interface ApiResponse<T> {
  data: T;
  status: string;
  links?: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
  meta?: PaginationMeta;
}

type OrderStatusType = 'draft' | 'created' | 'processing' | 'completed' | 'cancelled' | 'failed';
type PaymentStatusType = 'pending' | 'paid' | 'failed' | 'refunded' | 'partially_refunded';
type ShipmentStatusType = 'pending' | 'ready' | 'scheduled_for_pickup' | 'in_transit' | 'out_for_delivery' | 'delivered' | 'exception' | 'cancelled';

// Function to get orders with filters
const getOrders = async (filters: OrderFilters = {}): Promise<ApiResponse<OrderResponse[]>> => {
  const response = await api.get('/admin/orders', { params: filters });
  return response.data;
};

// Function to get a single order
const getOrder = async (id: number | string): Promise<ApiResponse<OrderResponse>> => {
  const response = await api.get(`/admin/orders/${id}`);
  return response.data;
};

// Function to get order statistics
const getOrderStats = async (filters: OrderFilters = {}): Promise<ApiResponse<OrderStats>> => {
  const response = await api.get('/admin/orders/stats', { params: filters });
  return response.data;
};

// Function to update an order
const updateOrder = async ({ id, data }: { id: number | string, data: Partial<OrderResponse> }): Promise<ApiResponse<OrderResponse>> => {
  const response = await api.put(`/admin/orders/${id}`, data);
  return response.data;
};

// Function to delete an order
const deleteOrder = async (id: number | string): Promise<ApiResponse<any>> => {
  const response = await api.delete(`/admin/orders/${id}`);
  return response.data;
};

export const useAdminOrders = (filters: OrderFilters = {}) => {
  const queryClient = useQueryClient();
  
  // Query to get orders list
  const { 
    data: response,
    isLoading,
    error,
    refetch
  } = useQuery<ApiResponse<OrderResponse[]>>({
    queryKey: ['admin', 'orders', filters],
    queryFn: () => getOrders(filters),
  });
  
  // Extract data and meta
  const orders = response?.data || [];
  const pagination = response?.meta;
  const totalOrders = pagination?.total || 0;
  
  // Query to get order stats
  const {
    data: statsResponse,
    isLoading: isLoadingStats
  } = useQuery<ApiResponse<OrderStats>>({
    queryKey: ['admin', 'orders', 'stats', filters.user_id],
    queryFn: () => getOrderStats({ user_id: filters.user_id }),
  });
  
  const stats = statsResponse?.data || {
    total_orders: 0,
    pending_orders: 0,
    processing_orders: 0,
    shipped_orders: 0,
    delivered_orders: 0,
    cancelled_orders: 0,
    total_revenue: 0
  };
  
  // Mutation to update order
  const updateOrderMutation = useMutation({
    mutationFn: updateOrder,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'order', data.data.id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders', 'stats'] });
    }
  });
  
  // Mutation to delete order
  const deleteOrderMutation = useMutation({
    mutationFn: deleteOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders', 'stats'] });
    }
  });
  
  // Update order status
  const updateOrderStatus = (id: number | string, status: OrderStatusType) => {
    return updateOrderMutation.mutateAsync({ 
      id, 
      data: { 
        order_status: status 
      } 
    });
  };
  
  // Update shipment status
  const updateShipmentStatus = (id: number | string, status: ShipmentStatusType) => {
    return updateOrderMutation.mutateAsync({ 
      id, 
      data: { 
        shipment_status: status 
      } 
    });
  };
  
  // Update payment status
  const updatePaymentStatus = (id: number | string, status: PaymentStatusType) => {
    return updateOrderMutation.mutateAsync({ 
      id, 
      data: { 
        payment_status: status 
      } 
    });
  };
  
  return {
    orders,
    pagination,
    totalOrders,
    stats,
    isLoading,
    isLoadingStats,
    error,
    refetch,
    updateOrder: (id: number | string, data: Partial<OrderResponse>) => 
      updateOrderMutation.mutateAsync({ id, data }),
    deleteOrder: (id: number | string) => deleteOrderMutation.mutateAsync(id),
    updateOrderStatus,
    updateShipmentStatus,
    updatePaymentStatus,
    isUpdating: updateOrderMutation.isPending,
    isDeleting: deleteOrderMutation.isPending
  };
};

// Custom hook to get a single order
export const useAdminOrder = (id: number | string) => {
  const queryClient = useQueryClient();
  
  const { 
    data: response,
    isLoading,
    error
  } = useQuery<ApiResponse<OrderResponse>>({
    queryKey: ['admin', 'order', id],
    queryFn: () => getOrder(id),
    enabled: !!id
  });
  
  const order = response?.data;
  
  // Mutation to update order
  const updateOrderMutation = useMutation({
    mutationFn: (data: Partial<OrderResponse>) => updateOrder({ id, data }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'order', id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders', 'stats'] });
    }
  });
  
  // Update order status
  const updateOrderStatus = (status: OrderStatusType) => {
    return updateOrderMutation.mutateAsync({ 
      order_status: status 
    });
  };
  
  // Update shipment status
  const updateShipmentStatus = (status: ShipmentStatusType) => {
    return updateOrderMutation.mutateAsync({ 
      shipment_status: status 
    });
  };
  
  // Update payment status
  const updatePaymentStatus = (status: PaymentStatusType) => {
    return updateOrderMutation.mutateAsync({ 
      payment_status: status 
    });
  };
  
  return {
    order,
    isLoading,
    error,
    updateOrder: (data: Partial<OrderResponse>) => updateOrderMutation.mutateAsync(data),
    updateOrderStatus,
    updateShipmentStatus,
    updatePaymentStatus,
    isUpdating: updateOrderMutation.isPending
  };
}; 